import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useTenant, type TenantSlug } from "@/hooks/useTenant";

const ENV_BADGE_STYLES: Record<TenantSlug, { label: string; className: string }> = {
  dev: { label: "DEV", className: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  staging: { label: "STAGING", className: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30" },
  "pre-prod": { label: "PRE-PRODUCTION", className: "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30" },
  prod: { label: "", className: "" },
};

const ENV_BORDER_COLORS: Record<TenantSlug, string> = {
  dev: "",
  staging: "",
  "pre-prod": "",
  prod: "",
};

export function EnvironmentSwitcher({ variant = "header", dark = false }: { variant?: "header" | "sidebar"; dark?: boolean }) {
  const { activeTenant, setActiveTenant, tenants, isLoading } = useTenant();

  if (isLoading || tenants.length === 0) return null;

  if (variant === "sidebar") {
    const triggerClass = dark
      ? "w-full h-9 text-sm bg-sidebar-accent text-sidebar-foreground border-sidebar-border"
      : "w-full h-9 text-sm bg-muted text-foreground border-border";
    const contentClass = dark
      ? "bg-[hsl(var(--sidebar-background))] text-sidebar-foreground border-sidebar-border"
      : "bg-background text-foreground border-border";
    return (
      <Select value={activeTenant} onValueChange={(v) => setActiveTenant(v as TenantSlug)}>
        <SelectTrigger className={triggerClass}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className={contentClass}>
          {tenants.map((t) => (
            <SelectItem key={t.slug} value={t.slug}>
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Header variant: just the badge
  if (activeTenant === "prod") return null;

  const style = ENV_BADGE_STYLES[activeTenant];
  const darkClassName = "bg-white/15 text-white border-white/30";

  return (
    <Badge variant="outline" className={dark ? darkClassName : style.className}>
      {style.label}
    </Badge>
  );
}

export function useEnvironmentBorderClass(): string {
  const { activeTenant } = useTenant();
  return ENV_BORDER_COLORS[activeTenant];
}
